import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BadgeCheck,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
} from 'lucide-react'
import Swal from 'sweetalert2'
import Lang from '../../../config/lang'
import {
  confirmMyJobOffer,
  resendMyJobOfferOtp,
  verifyMyJobOfferOtp,
} from '../api'
import ApplicationMetaSummary from './ApplicationMetaSummary'
import ApplicationNotice from './ApplicationNotice'
import ApplicationProgressTimeline from './ApplicationProgressTimeline'

const getSecondsUntil = (value) => {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  return Math.max(0, Math.floor((date.getTime() - Date.now()) / 1000))
}

const formatStartDateDisplay = (value) => {
  if (!value) return ''

  const [year, month, day] = String(value).split('T')[0].split('-')

  if (!year || !month || !day) return value

  return `${day}/${month}/${year}`
}

const formatExpiryDateDisplay = (value, lang) => {
  if (!value) return '-'

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(lang === 'id' ? 'id-ID' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

const formatCurrencyAmountDisplay = (value, lang) => {
  if (value == null || Number.isNaN(Number(value))) return '-'

  return new Intl.NumberFormat(lang === 'id' ? 'id-ID' : 'en-US', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  })
    .format(Number(value))
    .replace(/\s/g, ' ')
}

const formatCurrencyDisplay = (value, lang) => {
  const amount = formatCurrencyAmountDisplay(value, lang)

  if (amount === '-') return '-'

  return `${amount} / ${Lang(lang, 'month')}`
}

const JobOfferCard = ({ application, lang, userToken, onRefresh }) => {
  const currentDate = new Date()
  const today = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`
  const jobOffer = application?.jobOffer || {}

  const [selectedStartDate, setSelectedStartDate] = useState(
    jobOffer?.startWorkDate
      ? String(jobOffer.startWorkDate).split('T')[0]
      : today,
  )
  const [hasAgreedCommitment, setHasAgreedCommitment] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [isConfirming, setIsConfirming] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [offerOtpSeconds, setOfferOtpSeconds] = useState(null)
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false)
  const [resendOtpSeconds, setResendOtpSeconds] = useState(0)
  const [isResendingOtp, setIsResendingOtp] = useState(false)
  const [isResendOtpBlocked, setIsResendOtpBlocked] = useState(false)
  const [currentOfferStatus, setCurrentOfferStatus] = useState(
    String(jobOffer?.status || '').trim(),
  )
  const [currentOfferOTPExpiredAt, setCurrentOfferOTPExpiredAt] = useState(
    jobOffer?.offerOTPExpiredAt || null,
  )
  const [
    currentOfferOTPResendLockedUntil,
    setCurrentOfferOTPResendLockedUntil,
  ] = useState(jobOffer?.offerOTPResendLockedUntil || null)
  const [offerOtpResendCount, setOfferOtpResendCount] = useState(
    Number(jobOffer?.offerOTPResendCount || 0),
  )
  const [isStartDateCalendarOpen, setIsStartDateCalendarOpen] = useState(false)
  const [startDateCalendarMonth, setStartDateCalendarMonth] = useState(
    new Date(),
  )
  const startDateCalendarRef = useRef(null)
  const otpInputRef = useRef(null)
  const lockedScrollYRef = useRef(0)

  useEffect(() => {
    const nextSelectedStartDate = jobOffer?.startWorkDate
      ? String(jobOffer.startWorkDate).split('T')[0]
      : today

    setSelectedStartDate(nextSelectedStartDate)

    const [year, month, day] = String(nextSelectedStartDate).split('-')
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day))

    if (
      year &&
      month &&
      day &&
      parsedDate instanceof Date &&
      !Number.isNaN(parsedDate.getTime())
    ) {
      setStartDateCalendarMonth(
        new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1),
      )
    }
  }, [jobOffer?.startWorkDate, today])

  const compensationBreakdown = [
    {
      label: Lang(lang, 'basic_salary'),
      value: formatCurrencyDisplay(jobOffer?.basicSalary, lang),
    },
    {
      label: Lang(lang, 'transport_allowance'),
      value: formatCurrencyDisplay(jobOffer?.transportAllowance, lang),
    },
    {
      label: Lang(lang, 'job_offer_positional_allowance'),
      value: formatCurrencyDisplay(jobOffer?.positionalAllowance, lang),
    },
    {
      label: Lang(lang, 'job_offer_operational_allowance'),
      value: formatCurrencyDisplay(jobOffer?.operationalAllowance, lang),
    },
    {
      label: Lang(lang, 'meal_allowance'),
      value: formatCurrencyDisplay(jobOffer?.mealAllowance, lang),
    },
  ]

  const commitmentStatements = [
    Lang(lang, 'job_offer_commitment_item_1'),
    Lang(lang, 'job_offer_commitment_item_2'),
    Lang(lang, 'job_offer_commitment_item_3'),
    Lang(lang, 'job_offer_commitment_item_4'),
  ]

  const agreedTakeHome = formatCurrencyAmountDisplay(
    jobOffer?.totalSalary,
    lang,
  )
  const locationLabel = jobOffer?.location || application?.location || '-'
  const progressSteps =
    application?.progressSteps || application?.overviewMeta?.progressSteps || []
  const probationPeriod =
    jobOffer?.probationMonth != null
      ? `${jobOffer.probationMonth} ${Lang(lang, 'month')} ${Lang(
          lang,
          'job_offer_probation_end_evaluation',
        )}`
      : Lang(lang, 'job_offer_probation_period_value')
  const primaryPositionLabel = String(
    jobOffer?.position || application?.title || '',
  ).trim()
  const employmentTypeLabel = String(
    jobOffer?.jobType || application?.jobType || '',
  ).trim()
  const offerPositionLabel =
    primaryPositionLabel && employmentTypeLabel
      ? `${primaryPositionLabel} — ${employmentTypeLabel}`
      : primaryPositionLabel || employmentTypeLabel || '-'
  const offerTopSummary = [
    {
      label: Lang(lang, 'position'),
      value: offerPositionLabel,
    },
    {
      label: Lang(lang, 'location'),
      value: locationLabel,
    },
    {
      label: Lang(lang, 'other_benefit'),
      value: String(jobOffer?.otherBenefit || '').trim() || '-',
    },
    {
      label: Lang(lang, 'probation_period'),
      value: probationPeriod,
    },
  ]
  const totalSalaryReceivedLabel = Lang(lang, 'total_salary_received')
  const companyLabel =
    application?.company != null && String(application.company).trim() !== ''
      ? application.company
      : '-'
  const currentStatus = currentOfferStatus
  const isJobOfferAvailable = jobOffer?.isAvailable === true
  const offerDeadlineSeconds = getSecondsUntil(jobOffer?.offerExpiredAt)
  const isOfferExpired =
    currentStatus === 'OfferExpired' ||
    currentStatus === 'PreOnBoardingExpired' ||
    (jobOffer?.offerExpiredAt && offerDeadlineSeconds === 0)
  const isOtpPending = currentStatus === 'OTPPending'
  const isStartDateLocked = jobOffer?.startWorkDate != null
  const deadlineTargetValue = jobOffer?.offerExpiredAt
  const deadlineExpiryText = formatExpiryDateDisplay(deadlineTargetValue, lang)
  const resendOtpCount = offerOtpResendCount
  const isOfferOtpExpired =
    isOtpPending && currentOfferOTPExpiredAt && offerOtpSeconds === 0
  const canResendOtp =
    isOfferOtpExpired ||
    (resendOtpSeconds <= 0 && resendOtpCount < 3 && !isResendOtpBlocked)
  const otpEmail = String(
    jobOffer?.candidateEmail || application?.email || '',
  ).trim()
  const selectedStartDateObject = useMemo(() => {
    if (!selectedStartDate) return null

    const [year, month, day] = String(selectedStartDate).split('-')
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day))

    return year &&
      month &&
      day &&
      parsedDate instanceof Date &&
      !Number.isNaN(parsedDate.getTime())
      ? parsedDate
      : null
  }, [selectedStartDate])
  const startDateTodayObject = useMemo(() => {
    const currentDate = new Date()
    return new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
    )
  }, [])
  const startDateMinimumMonth = useMemo(
    () =>
      new Date(
        startDateTodayObject.getFullYear(),
        startDateTodayObject.getMonth(),
        1,
      ),
    [startDateTodayObject],
  )
  const startDateCalendarWeeks = useMemo(() => {
    if (
      !(startDateCalendarMonth instanceof Date) ||
      Number.isNaN(startDateCalendarMonth.getTime())
    ) {
      return []
    }

    const year = startDateCalendarMonth.getFullYear()
    const month = startDateCalendarMonth.getMonth()
    const monthStart = new Date(year, month, 1)
    const monthEnd = new Date(year, month + 1, 0)
    const startDayIndex = (monthStart.getDay() + 6) % 7
    const totalDays = monthEnd.getDate()
    const calendarCells = []

    for (let index = 0; index < startDayIndex; index += 1) {
      calendarCells.push(null)
    }

    for (let day = 1; day <= totalDays; day += 1) {
      calendarCells.push(new Date(year, month, day))
    }

    while (calendarCells.length % 7 !== 0) {
      calendarCells.push(null)
    }

    return Array.from({ length: calendarCells.length / 7 }, (_, weekIndex) =>
      calendarCells.slice(weekIndex * 7, weekIndex * 7 + 7),
    )
  }, [startDateCalendarMonth])
  const startDateDayLabels = useMemo(() => {
    return Array.from({ length: 7 }, (_, index) =>
      new Intl.DateTimeFormat(lang === 'id' ? 'id-ID' : 'en-US', {
        weekday: 'short',
      }).format(new Date(2024, 0, index + 1)),
    )
  }, [lang])
  const startDateCalendarMonthLabel =
    !(startDateCalendarMonth instanceof Date) ||
    Number.isNaN(startDateCalendarMonth.getTime())
      ? ''
      : (() => {
          const monthLabel = new Intl.DateTimeFormat(
            lang === 'id' ? 'id-ID' : 'en-US',
            {
              month: 'long',
              year: 'numeric',
            },
          ).format(startDateCalendarMonth)

          return monthLabel
            ? monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)
            : ''
        })()
  const canGoToPreviousStartDateMonth = useMemo(() => {
    if (
      !(startDateCalendarMonth instanceof Date) ||
      Number.isNaN(startDateCalendarMonth.getTime())
    ) {
      return false
    }

    const currentMonth = new Date(
      startDateCalendarMonth.getFullYear(),
      startDateCalendarMonth.getMonth(),
      1,
    )

    return currentMonth > startDateMinimumMonth
  }, [startDateCalendarMonth, startDateMinimumMonth])

  useEffect(() => {
    if (!isJobOfferAvailable || isOfferExpired || isStartDateLocked) {
      setIsStartDateCalendarOpen(false)
    }
  }, [isJobOfferAvailable, isOfferExpired, isStartDateLocked])

  useEffect(() => {
    if (!isStartDateCalendarOpen) return undefined

    const handleMouseDown = (event) => {
      if (
        startDateCalendarRef.current &&
        !startDateCalendarRef.current.contains(event.target)
      ) {
        setIsStartDateCalendarOpen(false)
      }
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsStartDateCalendarOpen(false)
      }
    }

    document.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isStartDateCalendarOpen])

  useEffect(() => {
    if (isOtpPending) {
      setHasAgreedCommitment(true)
    }
  }, [isOtpPending])

  useEffect(() => {
    setCurrentOfferStatus(String(jobOffer?.status || '').trim())
    setCurrentOfferOTPExpiredAt(jobOffer?.offerOTPExpiredAt || null)
    setCurrentOfferOTPResendLockedUntil(
      jobOffer?.offerOTPResendLockedUntil || null,
    )
    setOfferOtpResendCount(Number(jobOffer?.offerOTPResendCount || 0))
  }, [
    jobOffer?.candidateJobOfferID,
    jobOffer?.status,
    jobOffer?.offerOTPExpiredAt,
    jobOffer?.offerOTPResendLockedUntil,
    jobOffer?.offerOTPResendCount,
  ])

  useEffect(() => {
    if (
      !isOtpModalOpen ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined
    }

    lockedScrollYRef.current = window.scrollY

    const previousBodyPosition = document.body.style.position
    const previousBodyTop = document.body.style.top
    const previousBodyWidth = document.body.style.width
    const previousBodyOverflow = document.body.style.overflow
    const previousBodyPaddingRight = document.body.style.paddingRight
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth

    document.body.style.position = 'fixed'
    document.body.style.top = `-${lockedScrollYRef.current}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    window.requestAnimationFrame(() => {
      if (otpInputRef.current) {
        otpInputRef.current.focus({ preventScroll: true })
      }
    })

    return () => {
      document.body.style.position = previousBodyPosition
      document.body.style.top = previousBodyTop
      document.body.style.width = previousBodyWidth
      document.body.style.overflow = previousBodyOverflow
      document.body.style.paddingRight = previousBodyPaddingRight

      window.scrollTo({
        top: lockedScrollYRef.current,
        left: 0,
      })
    }
  }, [isOtpModalOpen])

  useEffect(() => {
    setIsResendOtpBlocked(false)
  }, [jobOffer?.candidateJobOfferID, currentOfferOTPExpiredAt])

  useEffect(() => {
    const updateResendOtp = () => {
      const nextSeconds = getSecondsUntil(currentOfferOTPResendLockedUntil)
      setResendOtpSeconds(nextSeconds == null ? 0 : nextSeconds)
    }

    updateResendOtp()

    const intervalID = window.setInterval(updateResendOtp, 1000)

    return () => window.clearInterval(intervalID)
  }, [currentOfferOTPResendLockedUntil])

  useEffect(() => {
    const updateOfferOtp = () => {
      setOfferOtpSeconds(getSecondsUntil(currentOfferOTPExpiredAt))
    }

    updateOfferOtp()

    const intervalID = window.setInterval(updateOfferOtp, 1000)

    return () => window.clearInterval(intervalID)
  }, [currentOfferOTPExpiredAt])

  const handleConfirm = async () => {
    if (!isJobOfferAvailable || !jobOffer?.candidateJobOfferID) return

    if (!selectedStartDate || !hasAgreedCommitment) {
      Swal.fire({
        title: Lang(lang, 'error_title'),
        text: Lang(lang, 'job_offer_confirm_validation'),
        icon: 'error',
        timer: 2200,
        showConfirmButton: false,
        heightAuto: false,
        returnFocus: false,
        customClass: { popup: 'my-custom-popup' },
      })
      return
    }

    const activeOtpSeconds = getSecondsUntil(currentOfferOTPExpiredAt)
    const currentResendOtpSeconds = getSecondsUntil(
      currentOfferOTPResendLockedUntil,
    )

    if (isOtpPending && activeOtpSeconds > 0) {
      setOtpCode('')
      setIsResendOtpBlocked(false)
      setOfferOtpSeconds(activeOtpSeconds)
      setResendOtpSeconds(
        currentResendOtpSeconds == null ? 0 : currentResendOtpSeconds,
      )
      setIsOtpModalOpen(true)
      return
    }

    const requestedStartWorkDate = selectedStartDate

    setIsConfirming(true)

    const response = await confirmMyJobOffer({
      candidateJobOfferID: jobOffer.candidateJobOfferID,
      startWorkDate: requestedStartWorkDate,
      userToken,
    })

    setIsConfirming(false)

    if (typeof response === 'string') {
      Swal.fire({
        title: Lang(lang, 'error_title'),
        text: response,
        icon: 'error',
        timer: 2500,
        showConfirmButton: false,
        heightAuto: false,
        returnFocus: false,
        customClass: { popup: 'my-custom-popup' },
      })
      return
    }

    const nextResendOtpSeconds = getSecondsUntil(
      response?.data?.offerOTPResendLockedUntil,
    )

    setOtpCode('')
    setIsResendOtpBlocked(false)
    setCurrentOfferStatus(response?.data?.status || 'OTPPending')
    setCurrentOfferOTPExpiredAt(response?.data?.offerOTPExpiredAt || null)
    setCurrentOfferOTPResendLockedUntil(
      response?.data?.offerOTPResendLockedUntil || null,
    )
    setOfferOtpResendCount(Number(response?.data?.offerOTPResendCount || 0))
    setOfferOtpSeconds(getSecondsUntil(response?.data?.offerOTPExpiredAt))
    setResendOtpSeconds(
      nextResendOtpSeconds == null ? 120 : nextResendOtpSeconds,
    )
    setIsOtpModalOpen(true)
    setSelectedStartDate(requestedStartWorkDate)
  }

  const handleResendOtp = async () => {
    if (!isJobOfferAvailable || !jobOffer?.candidateJobOfferID) return

    if (!canResendOtp) return

    setIsResendingOtp(true)

    const response = await resendMyJobOfferOtp({
      candidateJobOfferID: jobOffer.candidateJobOfferID,
      startWorkDate: selectedStartDate,
      userToken,
    })

    setIsResendingOtp(false)

    if (typeof response === 'string') {
      setIsResendOtpBlocked(true)
      return
    }

    const nextResendOtpSeconds = getSecondsUntil(
      response?.data?.offerOTPResendLockedUntil,
    )

    setOtpCode('')
    setIsResendOtpBlocked(false)
    setCurrentOfferStatus(response?.data?.status || 'OTPPending')
    setCurrentOfferOTPExpiredAt(response?.data?.offerOTPExpiredAt || null)
    setCurrentOfferOTPResendLockedUntil(
      response?.data?.offerOTPResendLockedUntil || null,
    )
    setOfferOtpResendCount(Number(response?.data?.offerOTPResendCount || 0))
    setOfferOtpSeconds(getSecondsUntil(response?.data?.offerOTPExpiredAt))
    setResendOtpSeconds(
      nextResendOtpSeconds == null ? 120 : nextResendOtpSeconds,
    )
  }

  const handleVerifyOtp = async () => {
    if (!jobOffer?.candidateJobOfferID) return

    if (!selectedStartDate) {
      Swal.fire({
        title: Lang(lang, 'error_title'),
        text: Lang(lang, 'job_offer_confirm_validation'),
        icon: 'error',
        timer: 2200,
        showConfirmButton: false,
        heightAuto: false,
        returnFocus: false,
        customClass: { popup: 'my-custom-popup' },
      })
      return
    }

    if (!otpCode.trim()) {
      Swal.fire({
        title: Lang(lang, 'error_title'),
        text: Lang(lang, 'job_offer_otp_required'),
        icon: 'error',
        timer: 2200,
        showConfirmButton: false,
        heightAuto: false,
        returnFocus: false,
        customClass: { popup: 'my-custom-popup' },
      })
      return
    }

    setIsVerifyingOtp(true)

    const response = await verifyMyJobOfferOtp({
      candidateJobOfferID: jobOffer.candidateJobOfferID,
      otpCode: otpCode.trim(),
      startWorkDate: selectedStartDate,
      userToken,
    })

    setIsVerifyingOtp(false)

    if (typeof response === 'string') {
      if (response === 'OTP has expired') {
        setOtpCode('')
        setCurrentOfferOTPExpiredAt(new Date(Date.now() - 1000).toISOString())
        setOfferOtpSeconds(0)
        setIsResendOtpBlocked(false)
        return
      }

      Swal.fire({
        title: Lang(lang, 'error_title'),
        text: response,
        icon: 'error',
        timer: 2600,
        showConfirmButton: false,
        heightAuto: false,
        returnFocus: false,
        customClass: { popup: 'my-custom-popup' },
      })
      return
    }

    setOtpCode('')
    setResendOtpSeconds(0)
    setIsResendOtpBlocked(false)
    setIsOtpModalOpen(false)

    Swal.fire({
      title: Lang(lang, 'job_offer_otp_verified_title'),
      text: response?.message || Lang(lang, 'job_offer_otp_verified_desc'),
      icon: 'success',
      timer: 2200,
      showConfirmButton: false,
      heightAuto: false,
      returnFocus: false,
      customClass: { popup: 'my-custom-popup' },
    })

    if (typeof onRefresh === 'function') {
      await onRefresh()
    }
  }

  return (
    <>
      <div className='rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-5 md:p-[25px]'>
        <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
          <div className='min-w-0'>
            <h3 className='break-words text-[20px] font-medium leading-[28px] text-[#111827]'>
              {application?.title || '-'}
            </h3>

            <div className='mt-3 flex flex-wrap items-center'>
              <img
                src='/asset/icon/company.svg'
                alt='company'
                className='h-4 w-4'
              />
              <p className='ml-2 break-words text-[16px] font-normal leading-5 text-[#4B5563] uppercase'>
                {companyLabel}
              </p>
            </div>
          </div>

          <span
            className={
              application?.badgeClassName ||
              'inline-flex w-fit rounded-full border border-[#BFDBFE] bg-[#DBEAFE] px-[13px] py-[5px] text-[12px] font-medium leading-4 text-[#1D4ED8]'
            }
          >
            {application?.badgeLabel || Lang(lang, 'job_offer')}
          </span>
        </div>

        {!isJobOfferAvailable ? (
          <ApplicationNotice
            icon={<span className='text-[12px] font-bold leading-none'>!</span>}
            tone='warning'
            className='mt-8'
          >
            <p>{Lang(lang, 'job_offer_waiting_desc')}</p>
          </ApplicationNotice>
        ) : isOfferExpired ? (
          <ApplicationNotice
            icon={<span className='text-[12px] font-bold leading-none'>!</span>}
            tone='warning'
            className='mt-8'
          >
            <div className='flex flex-col gap-1'>
              <p className='font-semibold'>
                {Lang(lang, 'job_offer_expired_title')}
              </p>
              <p>{Lang(lang, 'job_offer_expired_desc')}</p>
            </div>
          </ApplicationNotice>
        ) : (
          <ApplicationNotice
            variant='countdown'
            className='mt-8'
            countdownLabel={Lang(lang, 'job_offer_deadline_title')}
            countdownTargetDate={deadlineTargetValue}
            countdownExpiryPrefix={Lang(lang, 'expires')}
            countdownExpiryText={deadlineExpiryText}
          />
        )}

        <div className='mt-8'>
          <ApplicationMetaSummary
            dateApplied={application?.dateApplied}
            location={locationLabel}
            lang={lang}
          />
        </div>

        <div className='mt-8'>
          <ApplicationProgressTimeline items={progressSteps} lang={lang} />
        </div>

        <div className='mt-8 rounded-[12px] border border-[#E5E7EB] bg-white p-4 sm:p-6'>
          <h4 className='text-[14px] font-bold leading-7 text-[#0F1729]'>
            {Lang(lang, 'offer_confirmation')}
          </h4>

          <div className='mt-6 h-px w-full bg-[#E5E7EB]' />

          <div className='mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4'>
            {offerTopSummary.map((item) => (
              <div key={item.label} className='min-w-0'>
                <p className='text-[10px] font-normal leading-5 text-[#6B7280]'>
                  {item.label}
                </p>
                <p className='mt-1 break-words text-[10px] font-bold leading-5 text-[#0F1729]'>
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          <div className='my-5 overflow-hidden rounded-[10px] border border-[#E2E8F0] bg-[#F1F5F9]'>
            <div className='grid gap-x-6 gap-y-4 px-[10px] py-4 sm:grid-cols-2 lg:grid-cols-5'>
              {compensationBreakdown.map((item) => (
                <div key={item.label} className='min-w-0'>
                  <p className='text-[11px] font-normal leading-5 text-[#6B7280]'>
                    {item.label}
                  </p>
                  <p className='mt-1 break-words text-[11px] font-normal leading-5 text-[#0F1729]'>
                    {item.value}
                  </p>
                </div>
              ))}
            </div>

            <div className='border-t border-[#E2E8F0] px-[14px] py-4'>
              <div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
                <p className='text-[12px] font-normal leading-[19.5px] text-[#0F1729]'>
                  {totalSalaryReceivedLabel}
                </p>
                <p className='text-[12px] font-bold leading-[22.5px] text-[#0F1729]'>
                  {agreedTakeHome}
                </p>
              </div>
            </div>
          </div>

          <ApplicationNotice
            showIconCircle={false}
            icon={
              <ShieldAlert className='h-[18px] w-[18px]' strokeWidth={1.8} />
            }
            tone='credential'
          >
            <p className='italic'>
              {Lang(lang, 'job_offer_confidentiality_notice')}
            </p>
          </ApplicationNotice>
        </div>

        <div className='mt-6 rounded-[24px] border border-[#E5E7EB] bg-white p-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-5 md:p-6'>
          <h4 className='text-[14px] font-bold leading-7 text-[#0F1729]'>
            {Lang(lang, 'start_date')}
          </h4>

          <div
            ref={startDateCalendarRef}
            className='relative mt-6 w-full max-w-[420px]'
          >
            <button
              type='button'
              onClick={() =>
                setIsStartDateCalendarOpen((prevOpen) => !prevOpen)
              }
              disabled={
                !isJobOfferAvailable || isOfferExpired || isStartDateLocked
              }
              aria-expanded={isStartDateCalendarOpen}
              aria-label={Lang(lang, 'start_date')}
              className='flex h-[40px] w-full cursor-pointer items-center justify-between rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-left shadow-[inset_0px_1px_2px_rgba(16,24,40,0.04)] transition hover:border-[#48494B] focus:border-[#48494B] focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-[#F3F4F6] disabled:text-[#6B7280] disabled:opacity-100'
            >
              <span className='text-[14px] font-normal leading-5 text-[#0F1729]'>
                {formatStartDateDisplay(selectedStartDate)}
              </span>
              <CalendarDays className='h-5 w-5 flex-shrink-0 text-[#667085]' />
            </button>

            {isStartDateCalendarOpen ? (
              <div className='absolute left-0 top-[48px] z-50 w-full max-w-[320px] rounded-[12px] border border-[#E5E7EB] bg-white px-3 py-3 shadow-[0_12px_32px_rgba(15,23,42,0.14)] sm:px-3.5 sm:py-3.5'>
                <div className='flex items-center justify-between'>
                  <button
                    type='button'
                    onClick={() =>
                      setStartDateCalendarMonth(
                        (prevMonth) =>
                          new Date(
                            prevMonth.getFullYear(),
                            prevMonth.getMonth() - 1,
                            1,
                          ),
                      )
                    }
                    disabled={!canGoToPreviousStartDateMonth}
                    className='rounded-md p-1.5 text-[#98A2B3] transition hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    <ChevronLeft className='h-4 w-4' strokeWidth={2} />
                  </button>

                  <p className='text-[15px] font-bold leading-7 text-[#1F2937]'>
                    {startDateCalendarMonthLabel}
                  </p>

                  <button
                    type='button'
                    onClick={() =>
                      setStartDateCalendarMonth(
                        (prevMonth) =>
                          new Date(
                            prevMonth.getFullYear(),
                            prevMonth.getMonth() + 1,
                            1,
                          ),
                      )
                    }
                    className='rounded-md p-1.5 text-[#98A2B3] transition hover:bg-[#F8FAFC]'
                  >
                    <ChevronRight className='h-4 w-4' strokeWidth={2} />
                  </button>
                </div>

                <div className='mt-3 grid grid-cols-7 gap-y-2.5 text-center'>
                  {startDateDayLabels.map((label) => (
                    <div
                      key={label}
                      className='text-[11px] font-normal leading-4 text-[#9CA3AF]'
                    >
                      {label}
                    </div>
                  ))}

                  {startDateCalendarWeeks.flat().map((dateValue, index) => {
                    if (!dateValue) {
                      return (
                        <div
                          key={`start-date-empty-${index}`}
                          className='h-[32px]'
                        />
                      )
                    }

                    const dateKey = `${dateValue.getFullYear()}-${String(
                      dateValue.getMonth() + 1,
                    ).padStart(2, '0')}-${String(dateValue.getDate()).padStart(
                      2,
                      '0',
                    )}`
                    const disabled =
                      dateValue.getTime() < startDateTodayObject.getTime()
                    const selected =
                      selectedStartDateObject instanceof Date &&
                      !Number.isNaN(selectedStartDateObject.getTime()) &&
                      dateValue.getFullYear() ===
                        selectedStartDateObject.getFullYear() &&
                      dateValue.getMonth() ===
                        selectedStartDateObject.getMonth() &&
                      dateValue.getDate() === selectedStartDateObject.getDate()

                    return (
                      <button
                        key={dateKey}
                        type='button'
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return

                          setSelectedStartDate(dateKey)
                          setIsStartDateCalendarOpen(false)
                        }}
                        className={`mx-auto flex h-[32px] w-[32px] items-center justify-center rounded-[8px] text-[13px] font-normal leading-5 transition ${
                          selected
                            ? 'bg-[#EA2427] text-white'
                            : disabled
                              ? 'cursor-not-allowed text-[#D1D5DB]'
                              : 'text-[#020817] hover:bg-[#FFF1F2]'
                        }`}
                      >
                        {dateValue.getDate()}
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}

            <p className='mt-3 whitespace-nowrap text-[clamp(10px,2.8vw,12px)] font-normal leading-4 text-[#6B7280]'>
              {Lang(lang, 'job_offer_start_date_note')}
            </p>
          </div>
        </div>

        <div className='mt-6 rounded-[24px] border border-[#E5E7EB] bg-white p-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-5 md:p-6'>
          <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
            <h4 className='text-[14px] font-bold leading-7 text-[#0F1729]'>
              {Lang(lang, 'commitment_statement')}
            </h4>

            <span className='inline-flex w-fit rounded-full border border-[#FECACA] bg-[#FEF2F2] px-4 py-2 text-[12px] font-semibold leading-5 text-[#EF4444]'>
              {Lang(lang, 'required')}
            </span>
          </div>

          <div className='mt-8 space-y-5 text-[12px] leading-5 text-[#0F1729]'>
            {commitmentStatements.map((statement, index) => (
              <p key={`${index + 1}-${statement}`}>
                {index + 1}. {statement}
              </p>
            ))}
          </div>

          <label className='mt-8 inline-flex max-w-full cursor-pointer items-center gap-2.5'>
            <button
              type='button'
              onClick={() => setHasAgreedCommitment(!hasAgreedCommitment)}
              disabled={!isJobOfferAvailable || isOfferExpired}
              className={`flex h-5 w-5 items-center justify-center rounded-full border transition ${
                hasAgreedCommitment
                  ? 'border-[#EA2427] bg-[#EA2427] text-white'
                  : 'border-[#D1D5DB] bg-white text-transparent'
              } disabled:cursor-not-allowed disabled:opacity-60`}
              aria-pressed={hasAgreedCommitment}
            >
              <BadgeCheck className='h-[13px] w-[13px]' strokeWidth={2.5} />
            </button>

            <span className='text-[12px] font-normal leading-4 text-[#6B7280]'>
              {Lang(lang, 'agree_commitment_statement')}
            </span>
          </label>
        </div>

        <div className='mt-8 flex justify-end'>
          <button
            type='button'
            disabled={
              !isJobOfferAvailable ||
              !selectedStartDate ||
              !hasAgreedCommitment ||
              isConfirming ||
              isOfferExpired
            }
            onClick={handleConfirm}
            className='w-full rounded-full bg-[#EA2427] px-6 py-3 text-sm font-semibold leading-5 text-white transition hover:bg-[#D91F22] disabled:cursor-not-allowed disabled:bg-[#FCA5A5] sm:min-w-[160px] sm:w-auto'
          >
            {isConfirming ? Lang(lang, 'loading') : Lang(lang, 'confirm')}
          </button>
        </div>
      </div>

      {isOtpModalOpen ? (
        <div className='fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/35 px-3 py-6 sm:items-center sm:px-4'>
          <div className='w-full max-w-[616px] rounded-[12px] border border-[#E5E7EB] bg-white p-4 sm:p-6'>
            <div className='flex items-center justify-between gap-4'>
              <h3 className='text-[16px] font-bold leading-6 text-[#0F1729] sm:text-[18px] sm:leading-7'>
                {Lang(lang, 'job_offer_identity_verification_title')}
              </h3>

              <button
                type='button'
                onClick={() => setIsOtpModalOpen(false)}
                className='flex h-8 w-8 items-center justify-center text-[34px] font-light leading-none text-[#667085] transition hover:text-[#0F1729]'
                aria-label={Lang(lang, 'cancel')}
              >
                ×
              </button>
            </div>

            <div className='mt-6 h-px w-full bg-[#E5E7EB]' />

            <ApplicationNotice
              showIconCircle={false}
              tone='info'
              icon={
                <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full border-2 border-[#1677FF] text-[16px] font-bold leading-none text-[#1677FF]'>
                  i
                </span>
              }
              className='mt-6 rounded-[14px] border-[#93C5FD] bg-[#EFF6FF]'
              paddingClassName='px-4 py-4 sm:px-5'
              contentClassName='gap-4 sm:items-center'
              textClassName='text-[14px] font-normal leading-5 text-[#0F1729] sm:text-[16px] sm:leading-7'
            >
              <p>
                {Lang(lang, 'job_offer_otp_sent_to')}{' '}
                <span className='font-bold'>{otpEmail || '-'}</span>
              </p>
            </ApplicationNotice>

            <div className='mt-6'>
              <h4 className='text-[16px] font-medium leading-7 text-[#111827]'>
                {Lang(lang, 'job_offer_signature_title')}
              </h4>
              <p className='mt-3 text-[14px] font-normal leading-5 text-[#4B5563]'>
                {Lang(lang, 'job_offer_signature_desc')}
              </p>
            </div>

            <div className='relative mx-auto mt-6 w-full max-w-[408px]'>
              <div className='flex justify-center gap-1.5 min-[380px]:gap-2 sm:gap-3'>
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={`offer-otp-${index + 1}`}
                    className={`flex h-[48px] w-[38px] items-center justify-center rounded-[10px] border-2 bg-white text-[20px] font-bold leading-6 text-[#0F1729] min-[380px]:w-[42px] sm:h-[64px] sm:w-[56px] sm:rounded-[12px] sm:text-[24px] sm:leading-7 ${
                      otpCode[index] ? 'border-[#D1D5DB]' : 'border-[#E5E7EB]'
                    }`}
                  >
                    {otpCode[index] || ''}
                  </div>
                ))}
              </div>
              <input
                ref={otpInputRef}
                type='text'
                value={otpCode}
                onChange={(event) =>
                  setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode='numeric'
                maxLength={6}
                className='absolute inset-0 h-full w-full cursor-text opacity-0'
                aria-label={Lang(lang, 'otp_code')}
              />
            </div>

            <div className='mt-6 flex justify-center'>
              <button
                type='button'
                onClick={handleResendOtp}
                disabled={isConfirming || isResendingOtp || !canResendOtp}
                className='text-center text-[14px] font-normal leading-5 text-[#6B7280] transition hover:text-[#0F1729] disabled:cursor-not-allowed disabled:text-[#6B7280] disabled:opacity-100'
              >
                {canResendOtp ? (
                  <>
                    {Lang(lang, 'job_offer_resend_prompt')}{' '}
                    {isResendingOtp
                      ? Lang(lang, 'loading')
                      : Lang(lang, 'job_offer_resend_otp')}
                  </>
                ) : (
                  <>
                    {Lang(lang, 'job_offer_request_otp_wait')}
                    {resendOtpSeconds > 0
                      ? ` (${resendOtpSeconds}${Lang(lang, 'second_abbrev')})`
                      : ''}
                  </>
                )}
              </button>
            </div>

            <div className='mt-6 flex justify-end'>
              <button
                type='button'
                onClick={handleVerifyOtp}
                disabled={isVerifyingOtp || otpCode.length !== 6}
                className='inline-flex h-[44px] w-full items-center justify-center rounded-[34px] bg-[#EA2427] px-6 py-[10px] text-center text-[16px] font-bold leading-6 text-white backdrop-blur-[20px] transition hover:bg-[#D91F22] disabled:cursor-not-allowed disabled:bg-[#FCA5A5] sm:w-auto sm:px-[50px] sm:text-[18px]'
              >
                {isVerifyingOtp
                  ? Lang(lang, 'loading')
                  : Lang(lang, 'verify_otp')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default JobOfferCard
